import React from 'react';
import { CircularProgress, Typography } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoIcon from '@mui/icons-material/Info';
import { useParams } from 'react-router-dom';
import { useGetCompaniesQuery } from '../../Companies/Service/companiesApi';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import { BankAccountsRoutes } from '../../../Router/path';
import { bankAccountFields } from '../fields';
import { useGetBankAccountByIdQuery, useUpdateBankAccountMutation } from '../Service/bankAccountsApi';
import { BankAccount } from '../Types/bankAccountTypes';

const emptyBankAccount: BankAccount = {
    id: 0,
    companyId: 0,
    accountNumber: '',
    bankName: '',
    bankBranch: '',
    swift: '',
    currency: '',
    description: '',
};

const UpdateBankAccount = () => {
    const { bankAccountId } = useParams<{ bankAccountId: string }>();
    const parsedBankAccountId = bankAccountId ? parseInt(bankAccountId, 10) : 0;
    const {
        data: bankAccountData,
        isLoading: isBankAccountLoading,
        error: bankAccountError,
    } = useGetBankAccountByIdQuery(parsedBankAccountId, {
        skip: !bankAccountId || Number.isNaN(parsedBankAccountId),
    });
    const {
        data: allCompanies,
        isLoading: isCompaniesLoading,
        isError: isCompaniesError,
    } = useGetCompaniesQuery();
    const [updateBankAccount, updateStatus] = useUpdateBankAccountMutation();

    const companyOptions =
        allCompanies?.map((company) => ({
            label: company.name,
            value: company.id,
        })) ?? [];

    const fields = bankAccountFields.map((field) =>
        field.name === 'companyId'
            ? {
                    ...field,
                    options: companyOptions,
              }
            : field
    );

    const entityForm = useEntityForm<BankAccount>({
        initialData: bankAccountData ?? emptyBankAccount,
        fields,
        submitFn: (data) =>
            updateBankAccount({
                id: parsedBankAccountId,
                data: {
                    ...data,
                    companyId: Number(data.companyId),
                },
            }).unwrap(),
        redirectUrl: BankAccountsRoutes.root,
        title: 'Edytuj konto bankowe',
        icon: <AccountBalanceIcon />,
        backUrl: BankAccountsRoutes.root,
        groupOrder: [FormRenderGroupTypes.basic],
        groupIcons: {
            Podstawowe: <InfoIcon />,
        },
        mutationStatus: updateStatus,
    });

    if (isBankAccountLoading || isCompaniesLoading) {
        return (
            <Typography align="center" sx={{ mt: 4 }}>
                <CircularProgress />
            </Typography>
        );
    }

    if (bankAccountError) {
        return (
            <Typography color="error" align="center" sx={{ mt: 4 }}>
                Nie udało się załadować danych konta bankowego.
            </Typography>
        );
        }

    if (isCompaniesError) {
        return (
            <Typography color="error" align="center" sx={{ mt: 4 }}>
                Nie udało się załadować listy firm.
            </Typography>
        );
    }

    return <EntityFormLayout {...entityForm} />;
};

export default UpdateBankAccount;